import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface User {
  id: number;
  email: string;
  name: string;
  username: string;
  translatorId: number;
  theme_setting: number | null;
  avatar_url: string;
  role: string;
  isAdmin: boolean;
  isTranslator: boolean;
  isRealModerator: boolean;
  status: string;
  registrationDate: string;
  lastLogin: string;
  showAdultContent: boolean;
  show_shopping_offers?: boolean;
  can_approve_deletion: boolean;
  translaterName?: string; // Optional, for translator-specific name
  alias?: string; // Optional, for user alias
  isMuted: boolean;
  has_password?: boolean; // Új mező a jelszó meglétének jelzésére
  appliedAsTranslator?: boolean; // Fordítónak való jelentkezés folyamatban
  provider?: string; // Optional, for OmniAuth provider
  banned?: boolean; // Optional, user banned status
  can_community_upload?: boolean; // Közösségi feltöltéshez szükséges jogosultság
}

interface TranslatorItem {
  id: number;
  showAdultContent: boolean;
  iconBootstrap?: string; // Optional icon class for Bootstrap icons
  supportLinks: {
    type: string;
    url: string;
    iconBootstrap?: string; // Optional icon class for Bootstrap icons
  }[];
}

interface AuthContextType {
  loading: boolean;
  isLoggedIn: boolean;
  login: (username: string) => void;
  logout: () => void;
  currentUser: User | null;
  isTranslator: boolean;
  translaterName?: string;
  name?: string;
  isAdmin: boolean;
  isRealModerator: boolean;
  translatorId?: number | null;
  showAdultContent: boolean;
  showShoppingOffers: boolean;
  theme_setting: number | null;
  avatar_url: string;
  can_approve_deletion: boolean; // Ezt is javítjuk
  isMuted: boolean;
  appliedAsTranslator: boolean; // Fordítónak való jelentkezés folyamatban
  can_community_upload?: boolean; // Közösségi feltöltéshez szükséges jogosultság

  refreshCurrentUser: () => Promise<void>;
  // A has_password a currentUser objektumon keresztül érhető el
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

declare global {
  interface Window {
    __INITIAL_CURRENT_USER__?: User | null;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const initialUser = typeof window !== 'undefined' ? window.__INITIAL_CURRENT_USER__ ?? null : null;

  const [translatorProfile, setTranslatorProfile] = useState<TranslatorItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(initialUser);
  const [isLoggedIn, setIsLoggedIn] = useState(!!initialUser);
  const [isTranslator, setIsTranslator] = useState(initialUser?.isTranslator ?? false);
  const [isAdmin, setIsAdmin] = useState(initialUser?.isAdmin ?? false);
  const [isRealModerator, setIsRealModerator] = useState(initialUser?.isRealModerator ?? false);
  const [translaterName, setTranslaterName] = useState(initialUser?.translaterName);
  const [showAdultContent, setshowAdultContent] = useState(initialUser?.showAdultContent ?? false);
  const [showShoppingOffers, setShowShoppingOffers] = useState(initialUser?.show_shopping_offers ?? true);
  const [can_approve_deletion, setApproveDeletion] = useState(initialUser?.can_approve_deletion ?? false);
  const [theme_setting, setThemeSetting] = useState<number | null>(initialUser?.theme_setting ?? null);
  const [isMuted, setIsMuted] = useState(initialUser?.isMuted ?? false);
  const [appliedAsTranslator, setAppliedAsTranslator] = useState(initialUser?.appliedAsTranslator ?? false);
  const [canCommunityUpload, setCanCommunityUpload] = useState(initialUser?.can_community_upload ?? false);

  const [translatorId, setTranslatorId] = useState<number | null>(initialUser?.translatorId ?? null);
  const [avatar_url, setAvatar] = useState<string>(initialUser?.avatar_url ?? "");

  useEffect(() => {
    if (currentUser?.isTranslator && currentUser.translatorId) {
      fetch(`/api/translators/${currentUser.translatorId}`, {
        headers: { Accept: "application/json" },
        credentials: "include",
      })
        .then((res) => (res.ok ? res.json() : Promise.reject("Nem sikerült")))
        .then((data) => setTranslatorProfile(data))
        .catch(() => setTranslatorProfile(null));
    } else {
      setTranslatorProfile(null);
    }
  }, [currentUser]);

  const updateTranslatorStatus = async (user: User | null) => {
    const resetTranslatorStatus = () => {
      setIsTranslator(false);
      setTranslatorId(null);
      setTranslatorProfile(null);
      setTranslaterName("");
      setApproveDeletion(false);

    };

    if (!user?.isTranslator || !user.translatorId) {
      resetTranslatorStatus();
      return;
    }

    try {
      const response = await fetch(`/api/translators/${user.translatorId}`, {
        headers: {
          Accept: "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) throw new Error("Translator not found");

      const translator = await response.json();
      setIsTranslator(true);
      setTranslatorId(translator.id);
      setTranslatorProfile(translator);
      setTranslaterName(translator.translaterName);
      setApproveDeletion(translator.approve_deletion);


    } catch (error) {
      resetTranslatorStatus();
    }
  };

  useEffect(() => {
    if (!initialUser) {
      refreshCurrentUser();
    } else {
      updateTranslatorStatus(initialUser);
    }
  }, [initialUser]);

  const refreshCurrentUser = async () => {
    try {
      const response = await fetch('/api/current_user', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });
      if (!response.ok) throw new Error('Not logged in');

      const data: User = await response.json();
      setCurrentUser(data);
      setIsLoggedIn(true);
      setTranslatorId(data.translatorId);
      setIsTranslator(data.isTranslator);
      setAvatar(data.avatar_url || "");
      setIsAdmin(data.isAdmin);
      setIsRealModerator(data.isRealModerator ?? false);
      setshowAdultContent(data.showAdultContent ?? false);
      setShowShoppingOffers(data.show_shopping_offers ?? true);
      setTranslaterName(data.translaterName);
      setApproveDeletion(data.can_approve_deletion);
      setThemeSetting(data.theme_setting);
      setIsMuted(data.isMuted ?? false);
      setAppliedAsTranslator(data.appliedAsTranslator ?? false);
      setCanCommunityUpload(data.can_community_upload ?? false);

      // A has_password a 'data' objektummal együtt bekerül a currentUser állapotba
      await updateTranslatorStatus(data);
    } catch (error) {
      // Csak akkor logoljuk a hibát, ha az nem a "Not logged in" üzenet.
      if (error instanceof Error && error.message !== 'Not logged in') {
        console.error("Error fetching user:", error);
      }
      setCurrentUser(null);
      setIsLoggedIn(false);
      setAvatar("");
      setIsAdmin(false);
      setIsRealModerator(false);
      setShowShoppingOffers(true);
      setIsTranslator(false);
      setTranslatorId(null);
      setshowAdultContent(false);
      setThemeSetting(null);
      setIsMuted(false);
      setAppliedAsTranslator(false);
      setCanCommunityUpload(false);

      setApproveDeletion(false);
    } finally {
      setLoading(false);
    }
  };

  const login = (username: string) => {
    // API auth esetén nem kell
  };

  const logout = () => {
    setIsLoggedIn(false);
    setCurrentUser(null);
    setIsTranslator(false);
    setIsAdmin(false);
    setIsRealModerator(false);
    setshowAdultContent(false);
    setTranslaterName("");
    setAvatar("");
    setThemeSetting(null);
    setIsMuted(false);
    setAppliedAsTranslator(false);
    setCanCommunityUpload(false);

    setApproveDeletion(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        loading,
        login,
        logout,
        currentUser,
        isTranslator,
        isAdmin,
        isRealModerator,
        translatorId,
        translaterName,
        avatar_url,
        refreshCurrentUser,
        showAdultContent,
        showShoppingOffers,
        theme_setting,

        isMuted,
        can_approve_deletion,
        appliedAsTranslator,
        can_community_upload: canCommunityUpload,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
